import { useState, useEffect, useCallback } from 'react';
import { getBranding } from '../scripts/branding';

export const useTheme = () => {
  const [theme, setTheme] = useState('dark'); // Default to dark

  const applyTheme = useCallback((selectedTheme) => {
    const branding = getBranding();
    const themeColors = branding.colors[selectedTheme];

    const root = document.documentElement;
    if (themeColors) {
      // Set CSS variables for all theme colors
      for (const [key, value] of Object.entries(themeColors)) {
        root.style.setProperty(`--color-${key}`, value);
      }
      // Set the page's root background to the card color for a layered effect
      root.style.setProperty('--color-page-background', themeColors.cardBackground);
      // The body gets the darker, main background color
      document.body.style.backgroundColor = themeColors.background;
    }
    if (branding.fonts) {
      root.style.setProperty('--font-main', branding.fonts.main);
    }
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
    const initialTheme = savedTheme || (prefersLight ? 'light' : 'dark');
    setTheme(initialTheme);
    applyTheme(initialTheme);
  }, [applyTheme]);

  // Separate effect for handling OS theme changes
  useEffect(() => {
    const mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('theme')) {
        const newTheme = e.matches ? 'dark' : 'light';
        setTheme(newTheme);
        applyTheme(newTheme);
      }
    };
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [applyTheme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  };

  return { theme, toggleTheme };
};